import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { getUserOrganizations } from '../../services/organizationService'
import { getProjectsByOrganization } from '../../services/projectService'

export default function Dashboard() {
  const { user } = useAuthStore()
  const [organizations, setOrganizations] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)),
    end: new Date()
  })

  useEffect(() => {
    loadData()
  }, [user])

  useEffect(() => {
    // Reload data when page gains focus (e.g., after accepting invitation)
    const handleFocus = () => {
      loadData()
    }
    
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user])

  const loadData = async () => {
    try {
      const { data: orgs } = await getUserOrganizations(user.id)
      setOrganizations(orgs || [])
      
      if (orgs && orgs.length > 0) {
        const orgId = orgs[0].id
        setSelectedOrg(orgId)
        const { data: projectData } = await getProjectsByOrganization(orgId)
        setProjects(projectData || [])
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date)
  }

  const handleDateRangeChange = (days) => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    setDateRange({ start, end })
    setShowDatePicker(false)
  }

  const stats = {
    totalProjects: projects.length,
    inReview: projects.filter(p => p.status === 'in_review').length,
    approved: projects.filter(p => p.status === 'approved').length,
    draft: projects.filter(p => p.status === 'draft').length,
  }

  return (
    <div className="p-8 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-white">Dashboard</h1>
          <p className="text-text-muted-light dark:text-text-muted-dark mt-1">
            Welcome back, {user?.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Picker */}
          <div className="hidden sm:block relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl px-4 py-2.5 items-center text-sm text-text-muted-light dark:text-text-muted-dark shadow-sm hover:border-primary transition-all"
            >
              <span className="material-symbols-outlined text-lg mr-2">calendar_today</span>
              {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
              <span className="material-symbols-outlined text-lg ml-2 opacity-50">keyboard_arrow_down</span>
            </button>
            
            {/* Date Picker Dropdown */}
            {showDatePicker && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowDatePicker(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-lg z-50 py-2">
                  <button
                    onClick={() => handleDateRangeChange(7)}
                    className="w-full px-4 py-2 text-left text-sm text-text-light dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Last 7 days
                  </button>
                  <button
                    onClick={() => handleDateRangeChange(30)}
                    className="w-full px-4 py-2 text-left text-sm text-text-light dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Last 30 days
                  </button>
                  <button
                    onClick={() => handleDateRangeChange(90)}
                    className="w-full px-4 py-2 text-left text-sm text-text-light dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Last 90 days
                  </button>
                  <button
                    onClick={() => handleDateRangeChange(365)}
                    className="w-full px-4 py-2 text-left text-sm text-text-light dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Last year
                  </button>
                </div>
              </>
            )}
          </div>
          <Link
            to="/projects"
            className="bg-primary hover:bg-primary-hover text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center shadow-lg shadow-primary/20 transition-all transform active:scale-95"
          >
            <span className="material-symbols-outlined text-xl mr-2">add</span>
            New Project
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {/* Total Projects Card */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-border-light dark:border-border-dark hover:border-primary/50 dark:hover:border-primary/50 transition-all group">
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
              <span className="material-symbols-outlined text-indigo-500 dark:text-indigo-400">folder_open</span>
            </div>
            <span className="material-symbols-outlined text-emerald-400 text-xl">trending_up</span>
          </div>
          <div>
            <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest mb-1 group-hover:text-indigo-400 transition-colors">
              Total Projects
            </p>
            <h3 className="text-3xl font-bold text-text-light dark:text-white tracking-tight">{stats.totalProjects}</h3>
          </div>
        </div>

        {/* In Review Card */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-border-light dark:border-border-dark hover:border-primary/50 dark:hover:border-primary/50 transition-all group">
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl">
              <span className="material-symbols-outlined text-amber-500 dark:text-amber-400">pending_actions</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest mb-1 group-hover:text-amber-400 transition-colors">
              In Review
            </p>
            <h3 className="text-3xl font-bold text-text-light dark:text-white tracking-tight">{stats.inReview}</h3>
          </div>
        </div>

        {/* Approved Card */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-border-light dark:border-border-dark hover:border-primary/50 dark:hover:border-primary/50 transition-all group">
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl">
              <span className="material-symbols-outlined text-emerald-500 dark:text-emerald-400">check_circle</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest mb-1 group-hover:text-emerald-400 transition-colors">
              Approved
            </p>
            <h3 className="text-3xl font-bold text-text-light dark:text-white tracking-tight">{stats.approved}</h3>
          </div>
        </div>

        {/* Drafts Card */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-border-light dark:border-border-dark hover:border-primary/50 dark:hover:border-primary/50 transition-all group">
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-500/10 rounded-xl">
              <span className="material-symbols-outlined text-blue-500 dark:text-blue-400">edit_note</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest mb-1 group-hover:text-blue-400 transition-colors">
              Drafts
            </p>
            <h3 className="text-3xl font-bold text-text-light dark:text-white tracking-tight">{stats.draft}</h3>
          </div>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-soft border border-border-light dark:border-border-dark flex-1 overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark">
          <h2 className="text-lg font-bold text-text-light dark:text-white">Recent Projects</h2>
          <Link
            to="/projects"
            className="text-xs font-bold text-primary hover:text-white dark:hover:text-white tracking-widest transition-colors uppercase"
          >
            View All
          </Link>
        </div>

        {projects.length === 0 ? (
          <div className="p-20 flex flex-col items-center justify-center text-center">
            <div className="w-20 h-20 bg-gray-50 dark:bg-white/5 border border-border-light dark:border-border-dark rounded-3xl flex items-center justify-center mb-6 text-text-muted-light dark:text-text-muted-dark">
              <span className="material-symbols-outlined text-5xl opacity-40">folder_off</span>
            </div>
            <h3 className="text-xl font-bold text-text-light dark:text-white mb-2">No projects yet</h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mb-8 text-sm max-w-sm mx-auto leading-relaxed">
              Start your journey with ProofFlow. Create your first project to begin collaborating and reviewing.
            </p>
            <Link
              to="/projects"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-xl text-sm font-bold flex items-center shadow-xl shadow-primary/30 transition-all transform active:scale-95"
            >
              <span className="material-symbols-outlined text-xl mr-2">add</span>
              Create Project
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-800/20 text-xs text-text-muted-light dark:text-text-muted-dark">
                  <th className="px-6 py-4 text-left font-semibold">Project</th>
                  <th className="px-6 py-4 text-left font-semibold">Client</th>
                  <th className="px-6 py-4 text-left font-semibold">Status</th>
                  <th className="px-6 py-4 text-right font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light dark:divide-border-dark">
                {projects.slice(0, 5).map((project) => (
                  <tr
                    key={project.id}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/projects/${project.id}`}
                        className="flex items-center gap-3 hover:text-primary transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-sm text-gray-400">folder</span>
                        </div>
                        <span className="font-medium text-sm truncate">{project.title}</span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-muted-light dark:text-text-muted-dark truncate max-w-[150px]">
                      {project.client_name || project.client_email || 'No client'}
                    </td>
                    <td className="px-6 py-4">
                      <ProjectStatusBadge status={project.status} />
                    </td>
                    <td className="px-6 py-4 text-right text-xs text-text-muted-light dark:text-text-muted-dark whitespace-nowrap">
                      {new Date(project.updated_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function ProjectStatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', color: 'gray' },
    in_review: { label: 'In Review', color: 'yellow' },
    approved: { label: 'Approved', color: 'green' },
    changes_requested: { label: 'Changes Requested', color: 'red' },
  }

  const config = statusConfig[status] || statusConfig.draft
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-500',
    yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-500',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-500',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-500',
  }

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  )
}
